import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  UpdateRecordUpdateComposerDefinitionDocument,
  type UpdateRecordUpdateComposerDefinitionMutation,
  type UpdateRecordUpdateComposerDefinitionMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { OverrideInputEntry } from "~/features/dataImportProjects/utils/recordUpdateOverride";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Persist record-update overrides. A successful save only means the overrides were
 * stored — the backend validates shape only, so collisions surface later via the
 * diagnosis rule. Callers should prompt the operator to re-run diagnosis then dry-run.
 */
export const useSetRecordUpdateOverrides = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [mutate, { loading }] = useMutation<
    UpdateRecordUpdateComposerDefinitionMutation,
    UpdateRecordUpdateComposerDefinitionMutationVariables
  >(UpdateRecordUpdateComposerDefinitionDocument);

  const setOverrides = useCallback(
    async (recordUpdateComposerDefinitionId: string, overrides: OverrideInputEntry[]): Promise<boolean> => {
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        await mutate({ variables: { input: { tenantId, recordUpdateComposerDefinitionId, overrides } } });
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      }
    },
    [mutate, tenantId, t],
  );

  return { setOverrides, saving: loading };
};
